import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetAccount } from '@/modules/profile/hooks/use-account';
import { assignUserToOrganization } from '@/modules/profile/services/accounts.service';
import { switchOrganization } from '@/modules/auth/services/auth.service';
import { useAuthStore } from '@/state/store/auth';
import { useToast } from '@/hooks/use-toast';
import { HttpError } from '@/lib/https';
import {
  clearOrgSelectionRequired,
  clearPendingSignupOrganizationId,
  persistSelectedOrganizationId,
} from '@/lib/auth/org-selection';

type AssignOptions = {
  /** Where to navigate after a successful assign+switch. Defaults to a full reload of the current URL. */
  redirectTo?: string;
};

type UseAssignToOrganizationOptions = {
  /** When false, skip the account query (e.g. force-org dialog closed). */
  enabled?: boolean;
};

/**
 * Assign the signed-in user to an existing org (if needed), switch session context, then reload.
 */
export const useAssignToOrganization = (options?: UseAssignToOrganizationOptions) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { data: account } = useGetAccount({ enabled: options?.enabled ?? true });
  const selectedOrgId = useAuthStore((state) => state.selectedOrgId);
  const setTokens = useAuthStore((state) => state.setTokens);
  const [isBusy, setIsBusy] = useState(false);

  const assignAndSwitch = useCallback(
    async (organizationId: string, options?: AssignOptions): Promise<boolean> => {
      const orgId = organizationId.trim();
      if (!orgId || isBusy) return false;

      try {
        setIsBusy(true);

        const userId = account?.itemId?.trim();
        const alreadyMember = account?.memberships?.some(
          (membership) => membership.organizationId === orgId
        );

        if (userId && !alreadyMember) {
          const rolesFromMembership = account?.memberships?.find((m) => m.roles?.length)?.roles;
          try {
            await assignUserToOrganization({
              userId,
              organizationId: orgId,
              roles: rolesFromMembership?.length ? rolesFromMembership : ['user'],
            });
          } catch (assignError) {
            const status = assignError instanceof HttpError ? assignError.status : undefined;
            if (status !== 400 && status !== 409) {
              throw assignError;
            }
          }
        }

        if (orgId !== selectedOrgId) {
          const response = await switchOrganization(orgId);
          setTokens({
            accessToken: response.access_token,
            refreshToken: (response.refresh_token || useAuthStore.getState().refreshToken) ?? '',
          });
        }

        persistSelectedOrganizationId(orgId);
        clearPendingSignupOrganizationId();
        clearOrgSelectionRequired();

        if (options?.redirectTo) {
          window.location.assign(options.redirectTo);
        } else {
          window.location.reload();
        }
        return true;
      } catch (error) {
        let description = t('SOMETHING_WENT_WRONG') || 'Something went wrong';
        if (error instanceof HttpError) {
          const errorData = error.error as
            | { error_description?: string; error?: string }
            | undefined;
          if (typeof errorData?.error_description === 'string') {
            description = errorData.error_description;
          } else if (typeof errorData?.error === 'string') {
            description = errorData.error;
          }
        } else if (error instanceof Error && error.message) {
          description = error.message;
        }

        toast({
          variant: 'destructive',
          title: 'Could not assign organization',
          description,
        });
        setIsBusy(false);
        return false;
      }
    },
    [account, isBusy, selectedOrgId, setTokens, t, toast]
  );

  return { account, assignAndSwitch, isBusy, selectedOrgId };
};
